import { Button, Form, Modal, Spinner, Tab, Tabs } from "react-bootstrap"
import { CustomTimeAgo, FeedbackButton } from "../../Commons/Functions"
import { db } from "../../firebase";
import { addDoc, collection, deleteDoc, doc, getDocs, serverTimestamp } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";

export default function MultiplayerModal(props) {
    const MAX_IDLE_TIME = 20 * 1000 * 60; // 20 minutes
    const [lobbies, setLobbies] = useState([]);
    const [loading, setLoading] = useState(false);

    // TODO: find a way to put this back into hosting
    const [hosting, setHosting] = useState(false);
    const [lobbyId, setLobbyId] = useState(null);
    const lobbyIdRef = useRef(null)
    const isAllowedToJoinOthers = !(props.gameOptions || props.playerTurnOrder.length > 1)
    const hasGameStarted = props.gameOptions || props.playerTurnOrder.length >= 4
    const stopHosting = async () => {
        if (lobbyId) {
            await deleteDoc(doc(db, "Lobbies", lobbyId));
            setLobbyId(null);
            setHosting(false)
        }
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
            deleteDoc(doc(db, "Lobbies", lobbyIdRef.current));
            lobbyIdRef.current = null
            setLobbyId(null);
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    useEffect(() => {
        lobbyIdRef.current = lobbyId
    }, [lobbyId])

    useEffect(() => {
        if (hasGameStarted) {
            stopHosting()
        }
    }, [props.gameOptions, props.playerTurnOrder.length])

    async function fetchLobbies() {
        setLoading(true);
        setLobbies([])
        const snapshot = await getDocs(collection(db, "Lobbies"));
        const now = Date.now();

        const activeLobbies = [];

        // Iterate over all lobbies
        for (const docSnap of snapshot.docs) {
            const lobby = { id: docSnap.id, ...docSnap.data() };
            const last = lobby.createdAt?.toMillis?.() ?? 0;
            const isStale = last > 0 && now - last > MAX_IDLE_TIME;

            if (isStale) {
                // Delete stale lobby
                try {
                    await deleteDoc(doc(db, "Lobbies", docSnap.id));
                    //console.log(`Deleted stale lobby: ${lobby.hostName}`);
                } catch (err) {
                    console.error(`Error deleting lobby ${lobby.id}`, err);
                }
            } else {
                if (last > 0 && lobby.game === props.selectedGame) {
                    lobby.createdAt = lobby.createdAt.toDate()
                    activeLobbies.push(lobby);
                }
            }
        }

        setLobbies(activeLobbies);
        setLoading(false);
    };

    function close() {
        if (hosting) {
            stopHosting()
        }
        props.handleClose()
    }

    if (!props.peerInstance.current) { return }

    return (
        <Modal
            className=""
            show={props.show}
            onHide={props.handleClose}
            backdrop={hosting ? "static" : true}
            centered
            onEntering={() => { fetchLobbies(); }}>

            <Modal.Header>
                <Modal.Title>Multiplayer</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h5>Connect with other players</h5>
                <div className="box-container my-4" style={{ backgroundColor: "var(--main-color)" }}>If you have issues connecting with your friends, please make sure that you are all using Chrome or Firefox.</div>

                {!hasGameStarted && (<Tabs
                    //defaultActiveKey={lastSelectedTab}
                    id="uncontrolled-tab-example"
                    className="box-container"
                    justify
                >
                    <Tab eventKey="lobbies" title="Public lobbies"
                        onEntering={() => { fetchLobbies(); }}
                    >
                        <div className="box-container flex-column">
                            <HostToggleLobby peerId={props.playerId} lobbyName={props.playerName} gameName={props.selectedGame} hosting={hosting} setHosting={setHosting} lobbyId={lobbyId} setLobbyId={setLobbyId} stopHosting={stopHosting} />
                            <hr className="my-3"></hr>

                            {!hosting && (
                                <div className="d-flex flex-column">
                                    <div className="d-flex justify-content-between">
                                        <p className="my-auto">Public lobbies for {props.selectedGame}</p>
                                        <Button onClick={fetchLobbies} disabled={loading}>
                                            {loading ? ("Refreshing...") : "Refresh Lobbies"}
                                        </Button>
                                    </div>

                                    {lobbies.length === 0 ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ height: "8rem" }}>
                                            {loading ? (
                                                <Spinner animation="border" role="status"></Spinner>
                                            ) : (
                                                <p>No lobbies found.</p>
                                            )}
                                        </div>
                                    ) : (
                                        <ul className="p-0 mt-2">
                                            {lobbies.map((lobby) => (
                                                <li key={lobby.id} className="d-flex justify-content-between box-container brighter mx-0" >
                                                    <div className="d-flex flex-column ms-2">
                                                        <strong>{lobby.hostName}</strong>
                                                        <p className="my-0"><i>Created <CustomTimeAgo date={lobby.createdAt} /></i></p>
                                                    </div>
                                                    <Button
                                                        className="my-auto"
                                                        variant="secondary"
                                                        disabled={!isAllowedToJoinOthers}
                                                        onClick={() => {
                                                            props.call(lobby.hostPeerId, true)
                                                            setTimeout(() => {
                                                                close()
                                                            }, 500)
                                                        }}
                                                        style={{ marginLeft: "1rem" }}
                                                    >
                                                        Join
                                                    </Button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>)}
                            {hosting && (
                                <div className="box-container brighter">
                                    <p className="text-center w-100">You can't join another lobby while hosting one.</p>
                                </div>)}
                        </div>
                    </Tab>
                    <Tab eventKey="direct" title="Direct connect"
                        onEntering={() => {
                        }}
                    >
                        <div className="box-container flex-column">
                            <div className='d-flex flex-column align-items-center'>
                                <p className='m-0 w-100'>Send your id to other players</p>
                                <div className="box-container w-100 mx-0 px-0">
                                    <p className='d-flex align-items-center brighter px-2 w-75 text-nowrap'>{props.playerId}</p>
                                    <FeedbackButton className="w-25" feedBack={"Copied !"} onClick={() => {
                                        navigator.clipboard.writeText(props.playerId).then(function () {
                                        }).catch(function (err) {
                                            console.error("Erreur lors de la copie : ", err);
                                        });
                                    }}>Copy</FeedbackButton>
                                </div>
                            </div>

                            <div className='d-flex flex-column align-items-center'>
                                <p className='m-0 w-100'>Or enter their id</p>
                                <div className="box-container w-100 mx-0 px-0">
                                    <Form.Control
                                        type="text"
                                        id="peer-connect-id"
                                        defaultValue={""}
                                        className='w-75'
                                    />
                                    <FeedbackButton className="w-25" onClick={() => {
                                        let callingId = document.getElementById("peer-connect-id").value
                                        props.call(callingId, true)
                                        setTimeout(() => {
                                            close()
                                        }, 500)
                                    }}
                                        feedBack="Connecting"
                                        disabled={!isAllowedToJoinOthers}
                                    >Connect</FeedbackButton>
                                </div>
                            </div>
                        </div>
                    </Tab>
                </Tabs>)}
                {hasGameStarted && (
                    <div className="box-container flex-column">
                        <p className="mb-3">Game has already started, you can't join other players. But other players can still join you using direct connect.</p>
                        <div className="box-container w-100 mx-0 px-0">
                            <p className='d-flex align-items-center brighter px-2 w-75 text-nowrap'>{props.playerId}</p>
                            <FeedbackButton className="w-25" feedBack={"Copied !"} onClick={() => {
                                navigator.clipboard.writeText(props.playerId).then(function () {
                                }).catch(function (err) {
                                    console.error("Erreur lors de la copie : ", err);
                                });
                            }}>Copy</FeedbackButton>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => close()}>
                    {hosting ? "Hide lobby and close" : "Close"}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}


function HostToggleLobby({ peerId, lobbyName, gameName, hosting, setHosting, lobbyId, setLobbyId, stopHosting }) {
    useEffect(() => {
        return () => {
            if (hosting) {
                stopHosting(); // Clean up on unmount
            }
        };
    }, [hosting]);

    const startHosting = async () => {
        const docRef = await addDoc(collection(db, "Lobbies"), {
            hostName: lobbyName,
            game: gameName,
            hostPeerId: peerId,
            createdAt: serverTimestamp()
        });
        //console.log(`Created lobby with id ${docRef.id}`)
        setLobbyId(docRef.id);
    };

    const handleToggle = async (enabled) => {
        setHosting(enabled);
        if (enabled) {
            await startHosting();
        } else {
            await stopHosting();
        }
    };

    return (
        <div style={{ height: "3rem" }}>
            <Form.Check
                type="switch"
                id="hosting-switch"
                checked={hosting}
                label="Host Lobby"
                onChange={(e) => handleToggle(e.target.checked)}
            />
            {hosting && <p><i>Your lobby is now visible to others.</i></p>}
        </div>
    );
};
